import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
  type ClinicFeatures,
} from "../../shared/api/clinicFeatures";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleRow } from "./components/ToggleRow";

/** Cài đặt → Phòng khám → Hình thức khám (ADR-0027). */
export function ExamModesSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
  });

  const [form, setForm] = useState<ClinicFeatures | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (next: ClinicFeatures) => clinicFeaturesApi.update(next),
    onSuccess: (data) => {
      qc.setQueryData(clinicFeaturesQueryKeys.current(), data);
      setForm(data);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (e) => setError(apiError(e).detail),
  });

  if (!form) {
    return (
      <div>
        <PageHeader title="Hình thức khám" />
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Hình thức khám"
        description="Chọn các hình thức khám mà phòng khám triển khai. Khám dịch vụ luôn bật; bật Khám BHYT nếu cơ sở có khám chữa bệnh bảo hiểm y tế."
      />

      <Card className="max-w-2xl">
        <CardContent className="divide-y p-5">
          <ToggleRow
            label="Khám dịch vụ"
            description="Luôn bật — mọi lượt khám đều có thể là khám dịch vụ."
            checked
            locked
          />
          <ToggleRow
            label="Khám BHYT"
            description="Khi bật, mỗi lượt khám có thể chọn Dịch vụ hoặc BHYT; lượt khám BHYT sẽ nhập thông tin thẻ BHYT của bệnh nhân. (Xuất hồ sơ giám định BHYT là tính năng Pro.)"
            checked={form.bhytEnabled}
            onChange={(v) => setForm({ ...form, bhytEnabled: v })}
          />
        </CardContent>
      </Card>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-4 flex items-center gap-3">
        <Button
          type="button"
          disabled={save.isPending || !form}
          onClick={() => form && save.mutate(form)}
        >
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        {savedAt > 0 && !save.isPending && (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
            <Check size={16} /> Đã lưu
          </span>
        )}
      </div>
    </div>
  );
}
